import { getFileBlobUrl } from '../../../services/storage-service';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useAssistantFileViewerStore } from '../../../state/store/chat/use-assistant-file-viewer-store';
import { useEffect, useState } from 'react';
import { useConfig } from 'hooks/use-config';
import { Attachment, FileGenerationStatus, Message, MessageGeneration } from '../types/chat';
import { Download, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { allSupportedImageExtensions } from '../types/model.types';
import { DownloadAssistantFile } from './DownloadAssistantFile';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import {
  getAttachmentDisplayName,
  getAttachmentExtension,
  getAttachmentKindLabel,
} from '../utils/assistant-file-preview';
import {
  ASSISTANT_IMAGE_MARGIN_CLASS,
  ASSISTANT_IMAGE_SIZE_CLASS,
  ASSISTANT_IMAGE_WRAPPER_CLASS,
  ImageGenerationSkeleton,
} from './ImageGenerationSkeleton';


interface AssistantImagePreviewProps {
  file: Attachment;
  conversationId?: string;
  message?: Message;
  generation?: MessageGeneration;
  isGeneratedImage?: boolean; // true for generatedImages, false for attachments
  hasParticipants?: boolean; // true for group chat
}

export const AssistantAttachmentPreview = ({
  file,
  hasParticipants,
}: AssistantImagePreviewProps) => {
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const setItemIdFilePathMap = useChatStore((state) => state.setItemIdFilePathMap);
  const viewedFile = useAssistantFileViewerStore((state) => state.file);
  const openFile = useAssistantFileViewerStore((state) => state.openFile);
  const [isLoading, setIsLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [error, setError] = useState(false);
  const { configurationSettings } = useConfig();
  const theme = useTheme();

  const isImageFile = allSupportedImageExtensions.includes(`.${file?.extension?.toLowerCase()}`);
  const isViewingThisFile = viewedFile?.fileId === file.fileId;
  const canOpenViewer =
    !isImageFile && file.status !== FileGenerationStatus.IN_PROGRESS && Boolean(file.fileId);

  useEffect(() => {
    const fetchImage = async () => {
      if (!file?.fileId || !isImageFile) return;

      const filePath = itemIdFilePathMap.get(file.fileId);
      if (filePath) {
        setImageSrc(filePath);
        return;
      }

      if (file?.status && file?.status === FileGenerationStatus.IN_PROGRESS) {
        return;
      }

      setIsLoading(true);
      setError(false);

      try {
        const blobUrl = await getFileBlobUrl(file.fileId, configurationSettings, undefined);
        if (blobUrl) {
          setImageSrc(blobUrl);
          setItemIdFilePathMap(file.fileId, blobUrl);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Failed to fetch assistant image:', error);
        setError(true);
      }
      setIsLoading(false);
    };

    fetchImage();
  }, [
    file.fileId,
    file.status,
    configurationSettings,
    itemIdFilePathMap,
    setItemIdFilePathMap,
    isImageFile,
  ]);

  // Handle failed file generation - show toast and remove file
  // useEffect(() => {
  //   console.log('file in AssistantAttachmentPreview', file)

  //   if (file.status === FileGenerationStatus.FAILED) {

  //     // Show toast
  //     toast({
  //       variant: 'destructive',
  //       title: t('FILE_GENERATION_FAILED') || 'File Generation Failed',
  //       description: t('FAILED_TO_GENERATE_FILE', { fileName: file.fileName }) || `Failed to generate ${file.fileName}`,
  //     });

  //     // Remove file from the message generation
  //     if (conversationId && message && generation) {
  //       const currentState = useChatStore.getState();
  //       const currentConv = currentState.conversations[conversationId];
  //       if (currentConv) {
  //         const updatedMessages = currentConv.messages.map((msg) => {
  //           if (msg.recordId === message.recordId) {
  //             return {
  //               ...msg,
  //               generations: msg.generations.map((gen) => {
  //                 if (gen.id === generation.id) {
  //                   if (isGeneratedImage) {
  //                     // Remove from generatedImages
  //                     return {
  //                       ...gen,
  //                       generatedImages: gen.generatedImages?.filter(
  //                         (img) => img.fileId !== file.fileId
  //                       ),
  //                     };
  //                   } else {
  //                     // Remove from attachments
  //                     return {
  //                       ...gen,
  //                       attachments: gen.attachments?.filter(
  //                         (att) => att.fileId !== file.fileId
  //                       ),
  //                     };
  //                   }
  //                 }
  //                 return gen;
  //               }),
  //             };
  //           }
  //           return msg;
  //         });

  //         useChatStore.setState({
  //           conversations: {
  //             ...currentState.conversations,
  //             [conversationId]: {
  //               ...currentConv,
  //               messages: updatedMessages,
  //             },
  //           },
  //         });
  //       }
  //     }
  //   }
  // }, [file, conversationId, message, generation, isGeneratedImage]);

  const downloadFile = async () => {
    if (!imageSrc) return;

    const link = document.createElement('a');
    link.href = imageSrc;
    link.download = file.fileName;
    link.click();
  };

  if (!isImageFile) {
    const extension = getAttachmentExtension(file.fileName, file.extension);
    const displayName = getAttachmentDisplayName(file.fileName, file.extension);
    const extensionLabel = extension ? extension.toUpperCase() : 'FILE';
    const kindLabel = getAttachmentKindLabel(file.fileName, file.extension);
    const openViewer = () => {
      if (!canOpenViewer) return;
      openFile(file);
    };

    return (
      <div className="w-full">
        <div
          className={cn(
            'mt-2 flex w-full max-w-2xl items-center gap-3 rounded-xl border py-3.5 pl-6 pr-5',
            theme.theme === 'dark'
              ? 'border-[#3a3a3f] bg-[#27272C] text-true-white'
              : 'border-[#e5e5e5] bg-[#f7f7f7] text-neutral-900',
            isViewingThisFile &&
            (theme.theme === 'dark' ? 'border-neutral-500' : 'border-neutral-400')
          )}
        >
          <button
            type="button"
            disabled={!canOpenViewer}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openViewer();
            }}
            aria-label={`View ${file.fileName}`}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-3 bg-transparent text-left',
              canOpenViewer ? 'cursor-pointer' : 'cursor-default'
            )}
          >
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${theme.theme === 'dark' ? 'bg-[#323238]' : 'bg-white'
                }`}
            >
              <FileText
                className={`h-5 w-5 ${theme.theme === 'dark' ? 'text-neutral-300' : 'text-neutral-500'}`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={`truncate text-sm font-medium leading-5 ${theme.theme === 'dark' ? 'text-true-white' : 'text-neutral-900'
                  }`}
                title={file.fileName}
              >
                {displayName}
              </p>
              <p
                className={`truncate text-xs leading-4 ${theme.theme === 'dark' ? 'text-neutral-400' : 'text-neutral-500'
                  }`}
              >
                {kindLabel} · {extensionLabel}
              </p>
            </div>
          </button>
          <DownloadAssistantFile file={file} variant="labeled" />
        </div>
      </div>
    );
  }

  const imagePreviewWrapperClass = `flex flex-col gap-2 ${ASSISTANT_IMAGE_WRAPPER_CLASS} ${hasParticipants ? '' : ASSISTANT_IMAGE_MARGIN_CLASS}`;

  if (file.status && file.status === FileGenerationStatus.IN_PROGRESS) {
    return (
      <div className={imagePreviewWrapperClass}>
        <ImageGenerationSkeleton label="Creating image" />
      </div>
    );
  }

  // if (file.status && file.status === FileGenerationStatus.FAILED) {
  //   return null;
  // }


  if (isLoading) {
    return (
      <div className={imagePreviewWrapperClass}>
        <ImageGenerationSkeleton label="Loading image" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="flex items-center justify-between bg-gray-50 p-2 rounded mt-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-red-500">Failed to load image</span>
        </div>
      </div>
    );
  }

  return (
    <div className={imagePreviewWrapperClass}>
      <div className={`relative ${ASSISTANT_IMAGE_WRAPPER_CLASS} bg-gray-50 rounded overflow-hidden group`}>
        <img
          className={ASSISTANT_IMAGE_SIZE_CLASS}
          src={imageSrc}
          alt={file.fileName}
          data-file-id={file.fileId}
        />
        <Button
          size="sm"
          variant="outline"
          className={`absolute bottom-2 right-2 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white ${theme.theme === 'dark' ? 'hover:bg-white' : 'hover:bg-gray-100'}`}
          onClick={downloadFile}
          title="Download image"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
